import { Suspense } from "react";
import { Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import Card from "../Card";
import CollationMode from "./CollationMode";
import { ConditionBuilder } from "../ConditionBuilder";
import { Col, Row } from "../Grid";
import FormEditor from "../GridEditor";
import NumericInput from "../NumericInput";
import Select from "../Select";
import TextInput from "../TextInput";
import PropTypes from 'prop-types';

export const validDynamicPanelConfig = config => {
    return config?.title && config?.def?.length > 0 && config?.def.every(s => s.rows?.length > 0);
}

/**
 * A configuration editor for a data panel basedoff of a custom form
 * @component
 */
const DynamicPanelConfig = ({ config, fields, onChange, disabled = false }) => {
    const { t } = useTranslation();
    return <><Card title={t('panel')}>
        <Row>
            <Col md={6}>
                <TextInput value={config.title} placeholder={t('title')} disabled={disabled} required={true} onChange={title => onChange({ ...config, title })}></TextInput>
            </Col>
            <Col md={3}>
                <NumericInput value={config.panel?.w} placeholder={t('width')} disabled={disabled} onChange={w => onChange({ ...config, panel: { ...config.panel, w }})}></NumericInput>
            </Col>
            <Col md={3}>
                <NumericInput value={config.panel?.h} placeholder={t('height')} disabled={disabled} onChange={h => onChange({ ...config, panel: { ...config.panel, h }})}></NumericInput>
            </Col>
        </Row>
        <Row>
            <Col md={3}>
                <NumericInput value={config.panel?.minW} placeholder={t('minWidth')} disabled={disabled} onChange={minW => onChange({ ...config, panel: { ...config.panel, minW }})}></NumericInput>
            </Col>
            <Col md={3}>
                <NumericInput value={config.panel?.minH} placeholder={t('minHeight')} disabled={disabled} onChange={minH => onChange({ ...config, panel: { ...config.panel, minH }})}></NumericInput>
            </Col>
            <Col md={3}>
                <NumericInput value={config.panel?.maxW} placeholder={t('maxWidth')} disabled={disabled} onChange={maxW => onChange({ ...config, panel: { ...config.panel, maxW }})}></NumericInput>
            </Col>
            <Col md={3}>
                <NumericInput value={config.panel?.maxH} placeholder={t('maxHeight')} disabled={disabled} onChange={maxH => onChange({ ...config, panel: { ...config.panel, maxH }})}></NumericInput>
            </Col>
        </Row>
    </Card>
    <Card title={t('filter', { count: 1 })}>
        <Suspense fallback={<Spinner variant="primary"></Spinner>}>
            <ConditionBuilder
                fields={fields}
                query={config.query}
                onQueryChange={query => onChange({ ...config, query }) }
            ></ConditionBuilder>
        </Suspense>
    </Card>
    <Card title={t('select')}>
        <Row>
            <Col>
                <Select value={config.groupBy} placeholder={t('group')} onChange={groupBy => onChange({ ...config, groupBy, mapping: fields.find(f => f.name === groupBy)?.mapping })} wildcard={t('none')} options={fields} labelField="label" valueField="name"></Select>
            </Col>
        </Row>
        { config.groupBy === undefined || config.groupBy === '' ? null : <Row>
            <Col>
                <CollationMode collation={config.collation} onChange={collation => onChange({ ...config, collation })}></CollationMode>
            </Col>
        </Row> }
    </Card>
    <Card title={t('sort')}>
        <Row>
            <Col>
                { config.groupBy === undefined || config.groupBy === '' ? <Select value={config.sort} placeholder={t('field_one')} onChange={sort => onChange({ ...config, sort })} options={fields} labelField="label" valueField="name"></Select>
                 : <Select value={config.sort} placeholder={t('field_one')} onChange={sort => onChange({ ...config, sort })} options={[{value: "x", label: t('label')}, {value:"y", label: t('value')}]} labelField="label" valueField="value"></Select> }
            </Col>
        </Row>
    </Card>
    <Card title={t('form', { count: 1 })}>
        <FormEditor def={config.def ?? []} onChange={def => onChange({ ...config, def })}></FormEditor>
    </Card>
    </>
}

DynamicPanelConfig.propTypes = {
    config: PropTypes.object,
    fields: PropTypes.arrayOf(PropTypes.object).isRequired,
    onChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool
}

export default DynamicPanelConfig;